import React, { Component } from "react";
import { BASE_URL } from "../common/common";

const API_URL = BASE_URL;
const MAX_IMAGE_SIZE = 3 * 1024 * 1024; // 3 MB

const imgUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http")) return `${path}?ngrok-skip-browser-warning=true`;
  const cleanPath = path.startsWith("/") ? path.slice(1) : path;
  return `${API_URL}/${cleanPath}?ngrok-skip-browser-warning=true`;
};

// ════════════════════════════════
//  PHONE VALIDATION HELPER
// ════════════════════════════════
// Validates international phone numbers with country code
// Returns { valid: boolean, error?: string }
const validatePhone = (phone) => {
  if (!phone || !phone.trim()) {
    return { valid: false, error: "Phone number is required" };
  }

  // Strip spaces, dashes, parentheses for digit checks
  const cleaned = phone.replace(/[\s\-()]/g, "");

  // Must start with +
  if (!cleaned.startsWith("+")) {
    return { valid: false, error: "Phone must start with country code (e.g. +91)" };
  }

  const digitsOnly = cleaned.substring(1);

  // Only digits allowed after +
  if (!/^\d+$/.test(digitsOnly)) {
    return { valid: false, error: "Phone can only contain digits, +, -, spaces, ()" };
  }

  // Country-specific national-number length (excluding country code)
  // Add more entries here as needed.
  const countryRules = [
    { code: "1",   name: "US/Canada", length: 10 },
    { code: "44",  name: "UK",        length: 10 },
    { code: "91",  name: "India",     length: 10 },
    { code: "971", name: "UAE",       length: [8, 9] },
    { code: "966", name: "KSA",       length: 9 },
    { code: "968", name: "Oman",      length: 8 },
    { code: "974", name: "Qatar",     length: 8 },
    { code: "973", name: "Bahrain",   length: 8 },
    { code: "965", name: "Kuwait",    length: 8 },
    { code: "61",  name: "Australia", length: 9 },
    { code: "86",  name: "China",     length: 11 },
    { code: "81",  name: "Japan",     length: [10, 11] },
  ];

  // Find the longest matching country code (e.g. 971 before 9)
  const rule = countryRules
    .filter((r) => digitsOnly.startsWith(r.code))
    .sort((a, b) => b.code.length - a.code.length)[0];

  if (rule) {
    const national = digitsOnly.substring(rule.code.length);
    const expected = Array.isArray(rule.length) ? rule.length : [rule.length];

    if (!expected.includes(national.length)) {
      const expectedStr = expected.join(" or ");
      return {
        valid: false,
        error: `${rule.name} (+${rule.code}) requires ${expectedStr} digits — got ${national.length}`,
      };
    }
    return { valid: true };
  }

  // Generic fallback for unknown country codes — E.164: 8-15 total digits
  if (digitsOnly.length < 8 || digitsOnly.length > 15) {
    return { valid: false, error: "Phone number must be 8-15 digits including country code" };
  }
  return { valid: true };
};

class Contact extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // Offices
      officeView: "list",
      contactList: [],
      listLoading: false,
      editingContact: null,   // holds contact being edited
      offices: [
        { id: Date.now(), country: "", label: "", address: "", phones: [""], emails: [""], latitude: "", longitude: "", imagePreview: null, imageFile: null, saving: false },
      ],

      // Banner
      bannerView: "list",
      bannerList: [],
      bannerListLoading: false,
      bannerTitle: "Contact",
      bannerImagePreview: null,
      bannerFile: null,
      bannerSaving: false,
      editingBanner: null,   // holds banner being edited

      activeTab: "banner",
      successMessage: "",
      errorMessage: "",
    };
  }

  componentDidMount() {
    this.fetchContactList();
    this.fetchBannerList();
  }

  showSuccess = (msg) => {
    this.setState({ successMessage: msg, errorMessage: "" });
    setTimeout(() => this.setState({ successMessage: "" }), 3000);
  };

  showError = (msg) => {
    this.setState({ errorMessage: msg, successMessage: "" });
    setTimeout(() => this.setState({ errorMessage: "" }), 4000);
  };

  // ════════════════════════════════
  //  BANNER
  // ════════════════════════════════

  fetchBannerList = async () => {
    this.setState({ bannerListLoading: true });
    try {
      const res = await fetch(`${API_URL}/api/banners/getBanners`, {
        method: "GET",
        headers: { "ngrok-skip-browser-warning": "true" },
      });
      const json = await res.json();
      if (json.status) {
        const filtered = json.data.filter(
          (b) => b.Page_Name?.toLowerCase() === "contact"
        );
        this.setState({ bannerList: filtered, bannerListLoading: false });
      } else {
        this.setState({ bannerListLoading: false });
        this.showError("Failed to load banners.");
      }
    } catch (err) {
      this.setState({ bannerListLoading: false });
      this.showError("Cannot connect to API server.");
    }
  };

  // Open blank add form
  openBannerForm = () => {
    this.setState({ bannerView: "form", editingBanner: null, bannerTitle: "Contact", bannerImagePreview: null, bannerFile: null });
  };

  // Open edit form pre-filled
  openEditBanner = (banner) => {
    this.setState({
      bannerView: "form",
      editingBanner: banner,
      bannerTitle: banner.Page_Name || "Contact",
      bannerImagePreview: null,
      bannerFile: null,
    });
  };

  goToBannerList = () => {
    this.setState({ bannerView: "list", editingBanner: null });
    this.fetchBannerList();
  };

  handleBannerImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => this.setState({ bannerImagePreview: reader.result, bannerFile: file });
    reader.readAsDataURL(file);
  };

  removeBanner = () => this.setState({ bannerImagePreview: null, bannerFile: null });

  saveBanner = async () => {
    const { bannerTitle, bannerFile, editingBanner } = this.state;
    this.setState({ bannerSaving: true });
    try {
      const formData = new FormData();
      formData.append("page_Name", bannerTitle);
      if (bannerFile) formData.append("files", bannerFile);

      // If editing, append the id for update
      if (editingBanner) formData.append("BannerID", editingBanner.Banner_id);

      const url = editingBanner
        ? `${API_URL}/api/banners/updateBanner`
        : `${API_URL}/api/banners/addBanner`;

      const res = await fetch(url, { method: "POST", body: formData });
      const json = await res.json();
      this.setState({ bannerSaving: false });
      if (json.status) {
        this.showSuccess(editingBanner ? "Banner updated!" : "Banner saved!");
        setTimeout(() => this.goToBannerList(), 1000);
      } else {
        this.showError(json.message || "Save failed.");
      }
    } catch (err) {
      this.setState({ bannerSaving: false });
      this.showError("Cannot connect to API server.");
    }
  };

  deleteBanner = async (bannerId) => {
    if (!window.confirm("Are you sure you want to delete this banner?")) return;
    try {
      const res = await fetch(`${API_URL}/api/banners/deleteBanner`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: bannerId }),
      });
      const json = await res.json();
      if (json.message == "Banner deleted successfully") {
        this.showSuccess("Banner deleted.");
        this.fetchBannerList();
      } else {
        this.showError(json.message || "Delete failed.");
      }
    } catch (err) {
      this.showError("Cannot connect to API server.");
    }
  };

  // ════════════════════════════════
  //  CONTACT / OFFICES
  // ════════════════════════════════

  fetchContactList = async () => {
    this.setState({ listLoading: true });
    try {
      const res = await fetch(`${API_URL}/api/contact/getContact`, {
        method: "GET",
        headers: { "ngrok-skip-browser-warning": "true" },
      });
      const json = await res.json();
      if (json.status) {
        this.setState({ contactList: json.data.data[0], listLoading: false });
      } else {
        this.setState({ listLoading: false });
        this.showError("Failed to load contacts.");
      }
    } catch (err) {
      this.setState({ listLoading: false });
      this.showError("Cannot connect to API server.");
    }
  };

  // Open blank add form
  openAddForm = () => {
    this.setState({
      officeView: "form",
      editingContact: null,
      offices: [{ id: Date.now(), country: "", label: "", address: "", phones: [""], emails: [""], latitude: "", longitude: "", imagePreview: null, imageFile: null, saving: false }],
    });
  };

  // Open edit form pre-filled
  openEditContact = (contact) => {
    this.setState({
      officeView: "form",
      editingContact: contact,
      offices: [{
        id: contact.contact_id,
        country: contact.country || "",
        label: "",
        address: contact.Address || "",
        phones: contact.phone_no1 ? contact.phone_no1.split(",").map(p => p.trim()) : [""],
        emails: contact.email ? contact.email.split(",").map(e => e.trim()) : [""],
        latitude: contact.latitude || "",
        longitude: contact.longitude || "",
        existingImage: contact.image_url || contact.imageUrl || contact.image || null,
        imagePreview: null,
        imageFile: null,
        saving: false,
      }],
    });
  };

  goToList = () => {
    this.setState({ officeView: "list", editingContact: null });
    this.fetchContactList();
  };

  saveOffice = async (office) => {
    const { editingContact } = this.state;

    // ✅ Required field validations
    if (!office.country.trim()) return this.showError("Country is required.");
    if (!office.address.trim()) return this.showError("Address is required.");

    // ✅ Phone validation — country-code aware (e.g. +91 → 10 digits)
    const validPhones = office.phones.filter((p) => p.trim());
    if (validPhones.length === 0) {
      return this.showError("At least one phone number is required.");
    }
    for (let i = 0; i < validPhones.length; i++) {
      const result = validatePhone(validPhones[i]);
      if (!result.valid) {
        return this.showError(`Phone #${i + 1} (${validPhones[i]}): ${result.error}`);
      }
    }

    // ✅ Email validation — at least one valid email
    const validEmails = office.emails.filter((e) => e.trim());
    if (validEmails.length === 0) {
      return this.showError("At least one email is required.");
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    for (let em of validEmails) {
      if (!emailRegex.test(em)) {
        return this.showError(`Invalid email: ${em}`);
      }
    }

    this.updateOffice(office.id, "saving", true);
    try {
      // ✅ FormData — supports lat/lng + file upload
      const fd = new FormData();
      fd.append("country", office.country);
      fd.append("address", office.address);
      fd.append("phone_no1", office.phones.join(","));
      fd.append("email", office.emails.join(","));
      fd.append("lat", office.latitude);
      fd.append("long", office.longitude);
      if (office.imageFile) fd.append("image", office.imageFile);
      if (editingContact) fd.append("ContactID", editingContact.contact_id);

      const url = editingContact
        ? `${API_URL}/api/contact/updateContact`
        : `${API_URL}/api/contact/addContact`;

      const res = await fetch(url, { method: "POST", body: fd });
      const json = await res.json();
      this.updateOffice(office.id, "saving", false);
      if (json.status) {
        this.showSuccess(`${office.country || "Office"} ${editingContact ? "updated" : "saved"} successfully!`);
        setTimeout(() => this.goToList(), 1000);
      } else {
        this.showError(json.message || "Save failed.");
      }
    } catch (err) {
      this.updateOffice(office.id, "saving", false);
      this.showError("Cannot connect to API server.");
    }
  };

  deleteContact = async (contactId) => {
    if (!window.confirm("Are you sure you want to delete this contact?")) return;
    try {
      const res = await fetch(`${API_URL}/api/contact/deleteContact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: contactId }),
      });
      const json = await res.json();
      if (json.status) {
        this.showSuccess("Contact deleted.");
        this.fetchContactList();
      } else {
        this.showError(json.message || "Delete failed.");
      }
    } catch (err) {
      this.showError("Cannot connect to API server.");
    }
  };

  // ✅ Handle office image file selection
  handleOfficeImage = (id, e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > MAX_IMAGE_SIZE) {
      this.showError("Image must be less than 3 MB.");
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      this.setState((prev) => ({
        offices: prev.offices.map((o) =>
          o.id === id ? { ...o, imagePreview: reader.result, imageFile: file } : o
        ),
      }));
    };
    reader.readAsDataURL(file);
  };

  updateOffice = (id, field, value) => {
    this.setState((prev) => ({
      offices: prev.offices.map((o) => (o.id === id ? { ...o, [field]: value } : o)),
    }));
  };

  addOffice = () => {
    this.setState((prev) => ({
      offices: [...prev.offices, { id: Date.now(), country: "", label: "", address: "", phones: [""], emails: [""], latitude: "", longitude: "", imagePreview: null, imageFile: null, saving: false }],
    }));
  };

  removeOffice = (id) => {
    this.setState((prev) => ({ offices: prev.offices.filter((o) => o.id !== id) }));
  };

  // ✅ Phone input — strip everything except digits, +, -, space, ()
  updatePhone = (id, i, val) => {
    const cleaned = val.replace(/[^0-9+\-\s()]/g, "");
    this.setState((prev) => ({ offices: prev.offices.map((o) => { if (o.id !== id) return o; const p = [...o.phones]; p[i] = cleaned; return { ...o, phones: p }; }) }));
  };
  addPhone = (id) => this.setState((prev) => ({ offices: prev.offices.map((o) => o.id === id ? { ...o, phones: [...o.phones, ""] } : o) }));
  removePhone = (id, i) => this.setState((prev) => ({ offices: prev.offices.map((o) => o.id === id ? { ...o, phones: o.phones.filter((_, idx) => idx !== i) } : o) }));

  updateEmail = (id, i, val) => this.setState((prev) => ({ offices: prev.offices.map((o) => { if (o.id !== id) return o; const e = [...o.emails]; e[i] = val; return { ...o, emails: e }; }) }));
  addEmail = (id) => this.setState((prev) => ({ offices: prev.offices.map((o) => o.id === id ? { ...o, emails: [...o.emails, ""] } : o) }));
  removeEmail = (id, i) => this.setState((prev) => ({ offices: prev.offices.map((o) => o.id === id ? { ...o, emails: o.emails.filter((_, idx) => idx !== i) } : o) }));

  // ════════════════════════════════
  //  RENDER
  // ════════════════════════════════
  render() {
    const {
      officeView, contactList, listLoading, offices, editingContact,
      bannerView, bannerList, bannerListLoading, bannerTitle, bannerImagePreview, bannerSaving, editingBanner,
      activeTab, successMessage, errorMessage,
    } = this.state;

    return (
      <div>

        <div className="page-header">
          <h2>Contact Page Manager</h2>
          <p>Manage banner image and office addresses.</p>
        </div>

        {successMessage && <div style={s.success}>{successMessage}</div>}
        {errorMessage && <div style={s.error}>{errorMessage}</div>}

        {/* Tabs */}
        <div className="rsp-tab-row" style={s.tabRow}>
          {["banner", "offices"].map((tab) => (
            <button
              key={tab}
              style={activeTab === tab ? s.tabActive : s.tab}
              onClick={() => this.setState({ activeTab: tab })}
            >
              {tab === "banner" ? "Banner" : "Offices"}
            </button>
          ))}
        </div>

        {/* ══ BANNER TAB ══ */}
        {activeTab === "banner" && (
          <div>

            {/* Banner List */}
            {bannerView === "list" && (
              <div>
                <div className="rsp-list-top" style={s.listTopBar}>
                  <span style={s.countText}>
                    {bannerListLoading
                      ? "Loading..."
                      : `${bannerList.length} banner${bannerList.length !== 1 ? "s" : ""} found`}
                  </span>
                  {bannerList.length === 0 && (
                    <button
                      className="rsp-btn-save" style={s.btnSave}
                      onClick={() => this.openBannerForm()}
                    >
                      + Add Banner
                    </button>
                  )}
                </div>

                {bannerListLoading && <div style={s.emptyBox}><p style={s.emptyText}>Loading banners...</p></div>}

                {!bannerListLoading && bannerList.length === 0 && (
                  <div style={s.emptyBox}>
                    <p style={{ ...s.emptyText, marginBottom: "12px" }}>No banners found.</p>
                    <button className="rsp-btn-save" style={s.btnSave} onClick={this.openBannerForm}>+ Add First Banner</button>
                  </div>
                )}

                {!bannerListLoading && bannerList.map((banner, idx) => (
                  <div key={banner.Banner_id || idx} className="rsp-list-card" style={s.listCard}>

                    {/* Thumbnail */}
                    {banner.imageUrl ? (
                      <img src={imgUrl(banner.imageUrl)} alt="banner" className="rsp-thumb-img" style={s.thumbImg} />
                    ) : (
                      <div style={s.thumbPlaceholder}>No Image</div>
                    )}

                    {/* Details */}
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
                        <strong style={{ fontSize: "15px", color: "#1a1a2e" }}>{banner.Page_Name || "—"}</strong>
                        <span style={s.idBadge}>ID: {banner.Banner_id}</span>
                      </div>
                      <p style={{ fontSize: "11px", color: "#bbb" }}>
                        Added: {new Date(banner.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                      </p>
                    </div>

                    {/* Edit / Delete buttons */}
                    <div className="rsp-action-btns" style={s.actionBtns}>
                      <button className="rsp-btn-edit" style={s.btnEdit} onClick={() => this.openEditBanner(banner)}>Edit</button>
                      <button className="rsp-btn-delete" style={s.btnDelete} onClick={() => this.deleteBanner(banner.Banner_id)}>Delete</button>
                    </div>

                  </div>
                ))}
              </div>
            )}

            {/* Banner Form (Add / Edit) */}
            {bannerView === "form" && (
              <div>
                <div className="rsp-form-top" style={s.formTopBar}>
                  <button className="rsp-btn-back" style={s.btnBack} onClick={this.goToBannerList}>Back to List</button>
                  <h3 style={{ fontSize: "16px", color: "#1a1a2e", margin: 0 }}>
                    {editingBanner ? `Edit Banner — ID ${editingBanner.Banner_id}` : "Add New Banner"}
                  </h3>
                </div>

                <div className="card">

                  <div className="rsp-form-group" style={s.formGroup}>
                    <label style={s.label}>Banner Image</label>

                    {/* Show existing image when editing and no new file chosen */}
                    {editingBanner && !bannerImagePreview && editingBanner.imageUrl && (
                      <div style={{ marginBottom: "10px" }}>
                        <p style={{ fontSize: "12px", color: "#888", marginBottom: "6px" }}>Current image:</p>
                        <img src={imgUrl(editingBanner.imageUrl)} alt="current" style={{ width: "200px", height: "80px", objectFit: "cover", borderRadius: "6px", border: "1px solid #e8e8e8" }} />
                      </div>
                    )}

                    {bannerImagePreview ? (
                      <div className="rsp-preview-box" style={s.previewBox}>
                        <img src={bannerImagePreview} alt="Preview" style={s.previewImg} />
                        <div style={s.overlay}>
                          <button className="rsp-btn-delete" style={s.btnDelete} onClick={this.removeBanner}>Remove</button>
                          <label className="rsp-btn-dark" style={s.btnDark}>
                            Change
                            <input type="file" accept="image/*" onChange={this.handleBannerImage} style={{ display: "none" }} />
                          </label>
                        </div>
                      </div>
                    ) : (
                      <label className="rsp-upload-box" style={s.uploadBox}>
                        <p style={{ fontSize: "14px", color: "#555", marginBottom: "4px" }}>
                          {editingBanner ? "Click to replace banner image" : "Click to choose banner image"}
                        </p>
                        <p style={{ fontSize: "11px", color: "#aaa" }}>JPG, PNG, WEBP — Max 5MB — 1920x600px recommended</p>
                        <input type="file" accept="image/*" onChange={this.handleBannerImage} style={{ display: "none" }} />
                      </label>
                    )}
                  </div>

                  <div className="rsp-form-group" style={s.formGroup}>
                    <label style={s.label}>Banner Title</label>
                    <input disabled type="text" value={bannerTitle} onChange={(e) => this.setState({ bannerTitle: e.target.value })} placeholder="e.g. Contact Us" className="rsp-input" style={s.input} />
                  </div>

                  <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", borderTop: "1px solid #f0f0f0", paddingTop: "16px" }}>
                    <button className="rsp-btn-back" style={s.btnBack} onClick={this.goToBannerList}>Cancel</button>
                    <button className="rsp-btn-save" style={s.btnSave} onClick={this.saveBanner} disabled={bannerSaving}>
                      {bannerSaving ? "Saving..." : editingBanner ? "Update Banner" : "Save Banner"}
                    </button>
                  </div>

                </div>
              </div>
            )}
          </div>
        )}

        {/* ══ OFFICES TAB ══ */}
        {activeTab === "offices" && (
          <div>

            {/* Office List */}
            {officeView === "list" && (

            <div>

              {contactList.length < 2 && (
                    <div className="rsp-list-top" style={s.listTopBar}>
                  <span style={s.countText}>
                    {listLoading ? "Loading..." : `${contactList.length} contact${contactList.length !== 1 ? "s" : ""} found`}
                  </span>
                  <button className="rsp-btn-save" style={s.btnSave} onClick={this.openAddForm}>+ Add Contact</button>
                </div>
                  )}

                

                {listLoading && <div style={s.emptyBox}><p style={s.emptyText}>Loading contacts...</p></div>}

                {!listLoading && contactList.length === 0 && (
                  <div style={s.emptyBox}>
                    <p style={{ ...s.emptyText, marginBottom: "12px" }}>No contacts added yet.</p>
                    <button className="rsp-btn-save" style={s.btnSave} onClick={this.openAddForm}>+ Add First Contact</button>
                  </div>
                )}

                {!listLoading && contactList.map((contact, idx) => (
                  <div key={contact.contact_id || idx} className="rsp-list-card" style={s.listCard}>

                    {contact.image_url ? (
                      <img src={imgUrl(contact.image_url)} alt="office" className="rsp-thumb-img" style={s.thumbImg} />
                    ) : (
                      <div style={s.indexBadge}>{idx + 1}</div>
                    )}

                    {/* Details */}
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
                        <strong style={{ fontSize: "15px", color: "#1a1a2e" }}>{contact.country || "—"}</strong>
                        <span style={s.idBadge}>ID: {contact.contact_id}</span>
                      </div>
                      <p style={{ fontSize: "13px", color: "#555", marginBottom: "8px" }}>{contact.Address || "—"}</p>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "16px" }}>
                        <span style={s.metaText}>
                          <strong style={{ color: "#888" }}>Phone: </strong>
                          {contact.phone_no1 && contact.phone_no1.split(",").map((p, i) => (
                            <span key={i} style={{ marginRight: "8px" }}>{p.trim()}</span>
                          ))}
                        </span>
                        <span style={s.metaText}>
                          <strong style={{ color: "#888" }}>Email: </strong>
                          {contact.email && contact.email.split(",").map((e, i) => (
                            <span key={i} style={{ marginRight: "8px" }}>{e.trim()}</span>
                          ))}
                        </span>
                      </div>
                      <p style={{ fontSize: "11px", color: "#bbb", marginTop: "8px" }}>
                        Added: {new Date(contact.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                      </p>
                    </div>

                    {/* Edit / Delete buttons */}
                    <div className="rsp-action-btns" style={s.actionBtns}>
                      <button className="rsp-btn-edit" style={s.btnEdit} onClick={() => this.openEditContact(contact)}>Edit</button>
                      <button className="rsp-btn-delete" style={s.btnDelete} onClick={() => this.deleteContact(contact.contact_id)}>Delete</button>
                    </div>

                  </div>
                ))}
              </div>
            )}

            {/* Office Add / Edit Form */}
            {officeView === "form" && (
              <div>
                <div className="rsp-form-top" style={s.formTopBar}>
                  <button className="rsp-btn-back" style={s.btnBack} onClick={this.goToList}>Back to List</button>
                  <h3 style={{ fontSize: "16px", color: "#1a1a2e", margin: 0 }}>
                    {editingContact ? `Edit Contact — ${editingContact.country}` : "Add New Contact"}
                  </h3>
                </div>

                {/* Only show Add Another button when adding new */}
                {!editingContact && (
                  <div style={{ textAlign: "right", marginBottom: "16px" }}>
                    <button className="rsp-btn-dark" style={s.btnDark} onClick={this.addOffice}>+ Add Another Office</button>
                  </div>
                )}

                {offices.map((office, idx) => (
                  <div key={office.id} className="card" style={{ marginBottom: "18px" }}>

                    <div style={s.officeHead}>
                      <strong style={{ fontSize: "15px", color: "#1a1a2e" }}>
                        {editingContact ? `Edit — ${office.country || "Office"}` : `Office ${idx + 1} ${office.country ? `— ${office.country}` : ""}`}
                      </strong>
                      {!editingContact && offices.length > 1 && (
                        <button className="rsp-btn-delete" style={s.btnDelete} onClick={() => this.removeOffice(office.id)}>Remove</button>
                      )}
                    </div>

                    <div className="rsp-two-col" style={s.twoCol}>
                      <div className="rsp-form-group" style={s.formGroup}>
                        <label style={s.label}>Country *</label>
                        <input className="rsp-input" style={s.input} type="text" value={office.country} placeholder="e.g. India" onChange={(e) => this.updateOffice(office.id, "country", e.target.value)} />
                      </div>
                      <div className="rsp-form-group" style={s.formGroup}>
                        <label style={s.label}>Office Label</label>
                        <input className="rsp-input" style={s.input} type="text" value={office.label} placeholder="e.g. INDIA OFFICE" onChange={(e) => this.updateOffice(office.id, "label", e.target.value)} />
                      </div>
                    </div>

                    <div className="rsp-form-group" style={s.formGroup}>
                      <label style={s.label}>Address *</label>
                      <textarea className="rsp-textarea" style={s.textarea} value={office.address} placeholder="Full office address..." onChange={(e) => this.updateOffice(office.id, "address", e.target.value)} />
                    </div>

                    {/* ✅ Phone Numbers — with real-time validation feedback */}
                    <div className="rsp-form-group" style={s.formGroup}>
                      <label style={s.label}>
                        Phone Numbers *
                        <span style={{ color: "#aaa", fontWeight: "400", marginLeft: "6px" }}>
                          (with country code, e.g. +91 9899397141)
                        </span>
                      </label>
                      {office.phones.map((phone, i) => {
                        const validation = phone.trim() ? validatePhone(phone) : { valid: true };
                        const showError = phone.trim() && !validation.valid;
                        return (
                          <div key={i} style={{ marginBottom: "10px" }}>
                            <div style={s.inputRow}>
                              <input
                                style={{
                                  ...s.input,
                                  marginBottom: 0,
                                  borderColor: showError ? "#e53935" : "#ddd",
                                }}
                                type="tel"
                                inputMode="tel"
                                maxLength={20}
                                value={phone}
                                placeholder="+91 9899397141"
                                onChange={(e) => this.updatePhone(office.id, i, e.target.value)}
                              />
                              {office.phones.length > 1 && (
                                <button
                                  style={s.removeRowBtn}
                                  onClick={() => this.removePhone(office.id, i)}
                                >
                                  X
                                </button>
                              )}
                            </div>
                            {showError && (
                              <p style={s.fieldError}>⚠ {validation.error}</p>
                            )}
                          </div>
                        );
                      })}
                      <button style={s.addRowBtn} onClick={() => this.addPhone(office.id)}>+ Add Phone</button>
                    </div>

                    <div className="rsp-form-group" style={s.formGroup}>
                      <label style={s.label}>Email Addresses</label>
                      {office.emails.map((email, i) => (
                        <div key={i} style={s.inputRow}>
                          <input style={{ ...s.input, marginBottom: "8px" }} type="email" value={email} placeholder="email@tnaevents.com" onChange={(e) => this.updateEmail(office.id, i, e.target.value)} />
                          {office.emails.length > 1 && (
                            <button style={s.removeRowBtn} onClick={() => this.removeEmail(office.id, i)}>X</button>
                          )}
                        </div>
                      ))}
                      <button style={s.addRowBtn} onClick={() => this.addEmail(office.id)}>+ Add Email</button>
                    </div>

                    {/* ✅ Latitude + Longitude */}
                    <div className="rsp-two-col" style={s.twoCol}>
                      <div className="rsp-form-group" style={s.formGroup}>
                        <label style={s.label}>Latitude</label>
                        <input
                          className="rsp-input" style={s.input}
                          type="text"
                          value={office.latitude}
                          placeholder="e.g. 28.6139"
                          onChange={(e) => this.updateOffice(office.id, "latitude", e.target.value)}
                        />
                        <span style={s.hint}>Decimal degrees — e.g. 28.6139</span>
                      </div>
                      <div className="rsp-form-group" style={s.formGroup}>
                        <label style={s.label}>Longitude</label>
                        <input
                          className="rsp-input" style={s.input}
                          type="text"
                          value={office.longitude}
                          placeholder="e.g. 77.2090"
                          onChange={(e) => this.updateOffice(office.id, "longitude", e.target.value)}
                        />
                        <span style={s.hint}>Decimal degrees — e.g. 77.2090</span>
                      </div>
                    </div>

                    {/* ✅ Office Image Upload — Add & Edit */}
                    <div className="rsp-form-group" style={s.formGroup}>
                      <label style={s.label}>Office Image <span style={{ color: "#aaa", fontWeight: "400" }}>(optional)</span></label>

                      {/* Show current image when editing */}
                      {editingContact && !office.imagePreview && office.existingImage && (
                        <div style={{ marginBottom: "10px" }}>
                          <p style={s.hint}>Current image:</p>
                          <img src={imgUrl(office.existingImage)} alt="current" style={{ width: "180px", height: "90px", objectFit: "cover", borderRadius: "8px", border: "1px solid #e8e8e8" }} />
                        </div>
                      )}

                      {office.imagePreview ? (
                        <div style={{ display: "flex", gap: "14px", alignItems: "flex-start" }}>
                          <img src={office.imagePreview} alt="preview" style={{ width: "180px", height: "90px", objectFit: "cover", borderRadius: "8px", border: "1px solid #e8e8e8" }} />
                          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            <label className="rsp-btn-dark" style={s.btnDark}>
                              Change
                              <input type="file" accept="image/*" onChange={(e) => this.handleOfficeImage(office.id, e)} style={{ display: "none" }} />
                            </label>
                            <button className="rsp-btn-delete" style={s.btnDelete} onClick={() => this.setState((prev) => ({ offices: prev.offices.map((o) => o.id === office.id ? { ...o, imagePreview: null, imageFile: null } : o) }))}>
                              Remove
                            </button>
                          </div>
                        </div>
                      ) : (
                        <label className="rsp-upload-box" style={s.uploadBox}>
                          <p style={{ fontSize: "14px", color: "#555", marginBottom: "4px" }}>Click to upload office image</p>
                          <p style={{ fontSize: "11px", color: "#aaa" }}>JPG, PNG — Max 3MB</p>
                          <input type="file" accept="image/*" onChange={(e) => this.handleOfficeImage(office.id, e)} style={{ display: "none" }} />
                        </label>
                      )}
                    </div>

                    <div style={{ textAlign: "right", borderTop: "1px solid #f0f0f0", paddingTop: "16px" }}>
                      <button className="rsp-btn-save" style={s.btnSave} onClick={() => this.saveOffice(office)} disabled={office.saving}>
                        {office.saving ? "Saving..." : editingContact ? `Update ${office.country || "Contact"}` : `Save ${office.country || "Office"}`}
                      </button>
                    </div>

                  </div>
                ))}
              </div>
            )}

          </div>
        )}

      </div>
    );
  }
}

const s = {
  success: { background: "#e8f5e9", color: "#2e7d32", border: "1px solid #c8e6c9", borderRadius: "8px", padding: "12px 16px", marginBottom: "16px", fontSize: "14px" },
  error: { background: "#ffebee", color: "#c62828", border: "1px solid #ffcdd2", borderRadius: "8px", padding: "12px 16px", marginBottom: "16px", fontSize: "14px" },
  tabRow: { display: "flex", gap: "10px", borderBottom: "2px solid #e8e8e8", marginBottom: "20px" },
  tab: { padding: "10px 20px", background: "none", border: "none", borderBottom: "3px solid transparent", fontSize: "14px", color: "#888", cursor: "pointer", marginBottom: "-2px", fontWeight: "500" },
  tabActive: { padding: "10px 20px", background: "none", border: "none", borderBottom: "3px solid #e8a020", fontSize: "14px", color: "#e8a020", cursor: "pointer", marginBottom: "-2px", fontWeight: "600" },
  listTopBar: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" },
  countText: { fontSize: "14px", color: "#888" },
  listCard: { background: "white", borderRadius: "10px", padding: "18px 20px", border: "1px solid #e8e8e8", marginBottom: "12px", display: "flex", alignItems: "flex-start", gap: "14px" },
  indexBadge: { width: "28px", height: "28px", background: "#1a1a2e", color: "#e8a020", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", fontWeight: "700", flexShrink: 0, marginTop: "2px" },
  idBadge: { background: "#f0f2f5", color: "#888", border: "1px solid #e0e0e0", borderRadius: "10px", padding: "2px 8px", fontSize: "11px" },
  metaText: { fontSize: "12px", color: "#666" },
  emptyBox: { background: "white", borderRadius: "10px", padding: "48px 20px", border: "1px solid #e8e8e8", textAlign: "center" },
  emptyText: { color: "#aaa", fontSize: "14px" },
  thumbImg: { width: "100px", height: "60px", objectFit: "cover", borderRadius: "6px", border: "1px solid #e8e8e8", flexShrink: 0 },
  thumbPlaceholder: { width: "100px", height: "60px", background: "#f0f2f5", borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "11px", color: "#aaa", flexShrink: 0 },
  actionBtns: { display: "flex", flexDirection: "column", gap: "6px", flexShrink: 0, alignSelf: "center" },
  btnEdit: { background: "#e3f2fd", color: "#1565c0", border: "1px solid #bbdefb", padding: "6px 14px", borderRadius: "6px", fontSize: "12px", cursor: "pointer", fontWeight: "600", minWidth: "64px" },
  btnDelete: { background: "#ffebee", color: "#c62828", border: "1px solid #ffcdd2", padding: "6px 14px", borderRadius: "6px", fontSize: "12px", cursor: "pointer", fontWeight: "600", minWidth: "64px" },
  formTopBar: { display: "flex", alignItems: "center", gap: "14px", marginBottom: "16px" },
  formGroup: { marginBottom: "16px" },
  label: { display: "block", fontSize: "13px", color: "#555", fontWeight: "600", marginBottom: "6px" },
  input: { width: "100%", padding: "10px 14px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "14px", outline: "none", fontFamily: "Arial, sans-serif" },
  textarea: { width: "100%", padding: "10px 14px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "14px", outline: "none", minHeight: "80px", resize: "vertical", fontFamily: "Arial, sans-serif" },
  uploadBox: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", border: "2px dashed #ddd", borderRadius: "10px", padding: "36px 20px", cursor: "pointer", background: "#fafafa", textAlign: "center" },
  previewBox: { position: "relative", borderRadius: "10px", overflow: "hidden", border: "1px solid #e8e8e8" },
  previewImg: { width: "100%", height: "200px", objectFit: "cover", display: "block" },
  overlay: { position: "absolute", bottom: "10px", right: "10px", display: "flex", gap: "8px" },
  btnRed: { background: "#e53935", color: "#fff", border: "none", padding: "7px 14px", borderRadius: "6px", fontSize: "12px", cursor: "pointer", fontWeight: "600" },
  btnDark: { background: "#1a1a2e", color: "#fff", border: "none", padding: "7px 14px", borderRadius: "6px", fontSize: "12px", cursor: "pointer", fontWeight: "600" },
  btnSave: { background: "#e8a020", color: "#fff", border: "none", padding: "10px 20px", borderRadius: "8px", fontSize: "14px", fontWeight: "600", cursor: "pointer" },
  btnBack: { background: "#f0f2f5", color: "#333", border: "1px solid #ddd", padding: "9px 16px", borderRadius: "8px", fontSize: "13px", cursor: "pointer", fontWeight: "500" },
  twoCol: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" },
  officeHead: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "18px", paddingBottom: "12px", borderBottom: "1px solid #f0f0f0" },
  inputRow: { display: "flex", alignItems: "center", gap: "8px" },
  addRowBtn: { background: "#f0f2f5", color: "#555", border: "1px solid #ddd", padding: "7px 14px", borderRadius: "6px", fontSize: "12px", cursor: "pointer" },
  removeRowBtn: { background: "#fff0f0", color: "#e53935", border: "1px solid #ffcdd2", width: "36px", height: "38px", borderRadius: "6px", cursor: "pointer", flexShrink: 0 },
  hint: { fontSize: "11px", color: "#aaa", marginTop: "5px", display: "block" },
  // ✅ Field-level error message style
  fieldError: { fontSize: "11px", color: "#e53935", marginTop: "4px", marginLeft: "4px", fontWeight: "500" },
};

export default Contact;